import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent as CardContentMUI,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  MenuItem,
  Radio,
  CardActionArea,
  alpha,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  Payment as PaymentIcon,
  CardMembership as MembershipIcon,
  Scale as ScaleIcon,
  People as PeopleIcon,
  Verified as VerifiedIcon,
  PhoneIphone as PhoneIphoneIcon,
  Devices as DevicesIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import ApiService from "../../modules/APIService.interceptors";
import { CartContext } from "../../store/context/CartContext";
import NotAllowed from "../NotAllowed/NotAllowed";
import { useAuthStore } from "../../store/zustand/authStore";
import UserService from "../../services/user.service";
import { useNotifier } from "../../hooks/useNotifier";
import ProductService from "../../services/product.services";
import PaymentsService from "../../services/payment.service";
import DeviceSessionsService from "../../services/deviceSessions.service";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import UserPermissionsTable from "../../components/UserPermissionsTable/UserPermissionsTable";
import ManagePhone from "../../components/ManagePhone/ManagePhone";

// ==================== HELPERS ====================
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const shortHash = (value) => {
  if (!value) return "-";
  const s = String(value);
  if (s.length <= 16) return s;
  return `${s.slice(0, 8)}…${s.slice(-6)}`;
};

const isDateInRange = (date, start, end) => {
  const now = new Date(date);
  const startDate = new Date(start);
  const endDate = new Date(end);
  return now >= startDate && now <= endDate;
};

const getMembershipStatus = (membership) => {
  const now = new Date();
  const end = new Date(membership.end);
  const start = new Date(membership.start);

  if (now < start) return { label: "Futura", color: "info" };
  if (now > end) return { label: "Vencida", color: "error" };
  return { label: "Activa", color: "success" };
};

const getCurrentMembership = (memberships) => {
  if (!Array.isArray(memberships) || memberships.length === 0) return null;

  const now = new Date();
  const activeMemberships = memberships.filter(m =>
    isDateInRange(now, m.start, m.end)
  );

  if (activeMemberships.length === 0) return null;

  // Si hay varias activas, elegir la que tenga fecha de expiración más cercana
  return activeMemberships.reduce((closest, current) => {
    const closestEnd = new Date(closest.end);
    const currentEnd = new Date(current.end);
    return currentEnd < closestEnd ? current : closest;
  });
};

// ==================== PHONE HELPERS ====================
const countries = [
  { code: "AR", dialCode: "+54", label: "🇦🇷 Argentina (+54)" },
  { code: "UY", dialCode: "+598", label: "🇺🇾 Uruguay (+598)" },
  { code: "CL", dialCode: "+56", label: "🇨🇱 Chile (+56)" },
  { code: "PY", dialCode: "+595", label: "🇵🇾 Paraguay (+595)" },
  { code: "BO", dialCode: "+591", label: "🇧🇴 Bolivia (+591)" },
  { code: "BR", dialCode: "+55", label: "🇧🇷 Brasil (+55)" },
  { code: "PE", dialCode: "+51", label: "🇵🇪 Perú (+51)" },
  { code: "CO", dialCode: "+57", label: "🇨🇴 Colombia (+57)" },
  { code: "MX", dialCode: "+52", label: "🇲🇽 México (+52)" },
  { code: "US", dialCode: "+1", label: "🇺🇸 Estados Unidos (+1)" },
  { code: "ES", dialCode: "+34", label: "🇪🇸 España (+34)" },
];

const sanitizeE164ish = (raw) => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  const digits = trimmed.replace(/\D/g, "");
  return `+${digits}`;
};

const sortByDialDesc = [...countries].sort(
  (a, b) => b.dialCode.length - a.dialCode.length
);

const findCountryFromE164ish = (raw) => {
  const e164ish = sanitizeE164ish(raw);
  const match = sortByDialDesc.find((c) => e164ish.startsWith(c.dialCode));
  return match || countries.find((c) => c.code === "AR");
};

const splitPhone = (raw) => {
  const e164ish = sanitizeE164ish(raw);
  const country = findCountryFromE164ish(e164ish);
  const local = e164ish.startsWith(country.dialCode)
    ? e164ish.slice(country.dialCode.length).replace(/\D/g, "")
    : e164ish.replace(/^\+/, "");
  return { country, local };
};

const buildE164 = (dialCode, local) =>
  `${dialCode}${String(local || "").replace(/\D/g, "")}`;

const isValidE164 = (value) =>
  !!value && /^\+[1-9][0-9]{6,14}$/.test(value.trim());

// ==================== PACK HELPERS (from UserTable) ====================
const formatCurrencyPack = (value, currency) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "ARS" ? 0 : 2,
  }).format(value ?? 0);

const formatDateShort = (iso) =>
  iso ? new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" }) : "-";

const isPackAvailable = (pack) => {
  const disabledByType = String(pack?.product?.type || "").toLowerCase() === "disabled";
  if (disabledByType) return false;
  const now = new Date();
  const membershipEnds = new Date(pack?.membership?.end ?? 0);
  const workshops = Array.isArray(pack?.workshops) ? pack.workshops : [];
  const hasOnSale = workshops.some((w) => w?.status === "onSale");
  return workshops.length > 0 ? hasOnSale : membershipEnds > now;
};

const availabilityLabel = (pack) => {
  const disabledByType = String(pack?.product?.type || "").toLowerCase() === "disabled";
  if (disabledByType) return "Deshabilitado";
  return isPackAvailable(pack) ? "Disponible" : "No disponible";
};

// ==================== COMPONENTE PRINCIPAL ====================
const ProfileUser = () => {
  const { userId } = useParams();
  const { setTitleSeccion } = useContext(CartContext);
  const userPermission = useAuthStore((state) => state.user);

  const hasPermissionEditUser = Boolean(userPermission?.admin_users?.USERS?.WRITE?.[0]);

  const notify = useNotifier();

  // Estados principales
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Device Sessions
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState("");
  const [deviceSessions, setDeviceSessions] = useState([]);
  const [deviceSessionsStats, setDeviceSessionsStats] = useState(null);

  // Estados de edición
  const [editMode, setEditMode] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const [saving, setSaving] = useState(false);

  // Estados de verificación de teléfono
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [verifySuccess, setVerifySuccess] = useState(false);

  const initialSplit = useMemo(() =>
    splitPhone(userData?.user?.phone),
    [userData?.user?.phone]
  );
  const [verifyCountry, setVerifyCountry] = useState(initialSplit.country || countries[0]);
  const [verifyLocal, setVerifyLocal] = useState(initialSplit.local || "");
  const [verifyLocalError, setVerifyLocalError] = useState("");

  // Estados de gestión de pesos
  const [editingWeightId, setEditingWeightId] = useState(null);
  const [editedWeight, setEditedWeight] = useState({});
  const [openWeightDialog, setOpenWeightDialog] = useState(false);
  const [weightDialogMode, setWeightDialogMode] = useState("create"); // "create" | "edit"
  const [weightFormData, setWeightFormData] = useState({
    current_weight: "",
    objective_weight: "",
    date: new Date().toISOString().split('T')[0]
  });

  // Estados de modal de membresías
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [selectedMembershipId, setSelectedMembershipId] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);

  // Cargar datos del usuario
  const loadUserData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await UserService.getUserById(userId);

      if (response) {
        setUserData(response);
        setEditedUser(response.user || {});
        setPhoneVerified(!!(response.user?.phone_verified));
      } else {
        notify("Usuario no encontrado", "error");
      }
    } catch (error) {
      console.error("Error al cargar usuario:", error);
      notify("Error al cargar los datos del usuario", "error");
    } finally {
      setLoading(false);
    }
  }, [userId]); // Removido notify de las dependencias

  useEffect(() => {
    setTitleSeccion("Perfil de Usuario");
    loadUserData();
  }, [userId, setTitleSeccion]); // Removido loadUserData, solo depende de userId

  const loadDeviceSessions = useCallback(async () => {
    try {
      // setSessionsLoading(true);
      setSessionsError("");
      const [sessionsRes, statsRes] = await Promise.all([
        DeviceSessionsService.getUserSessions(userId),
        DeviceSessionsService.getUserStats(userId),
      ]);
      setDeviceSessions(Array.isArray(sessionsRes) ? sessionsRes : []);
      setDeviceSessionsStats(statsRes || null);
    } catch (e) {
      setSessionsError("No se pudieron cargar las sesiones del usuario.");
    } finally {
      setSessionsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // Carga lazy: solo cuando se abre la tab "Sesiones"
    if (activeTab === 4 && deviceSessions.length === 0 && !sessionsLoading) {
      loadDeviceSessions();
    }
  }, [activeTab, deviceSessions.length, sessionsLoading, loadDeviceSessions]);

  // Datos derivados
  const currentMembership = useMemo(() =>
    getCurrentMembership(userData?.memberships || []),
    [userData?.memberships]
  );

  const isPremium = useMemo(() =>
    currentMembership !== null,
    [currentMembership]
  );

  // Handlers de edición
  const handleEditToggle = () => {
    if (editMode) {
      setEditedUser(userData.user || {});
    }
    setEditMode(!editMode);
  };

  const handleFieldChange = (field, value) => {
    setEditedUser(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveUser = async () => {
    try {
      console.log("editedUser before update");
      console.log(editedUser);
      console.log("editedUser after update");
      setSaving(true);
      // Excluir phone de la actualización
      const { phone, phone_verified, ...userToUpdate } = editedUser;
      userToUpdate.dni = parseDniToNumber(userToUpdate.dni);
      await UserService.updateUser(userId, userToUpdate);
      notify("Usuario actualizado correctamente", "success");
      setEditMode(false);

      // Recargar datos
      const response = await UserService.getUserById(userId);
      if (response) {
        setUserData(response);
        setEditedUser(response.user || {});
        setPhoneVerified(!!(response.user?.phone_verified));
      }
    } catch (error) {
      console.error("Error al guardar:", error);
      notify("Error al actualizar el usuario", "error");
    } finally {
      setSaving(false);
    }
  };

 
  function parseDniToNumber(dni) {
    if (dni === "" || dni === null || dni === undefined) return null;
    const digits = String(dni).replace(/\D/g, "");
    if (!digits) return null;
    const n = Number(digits);
    return Number.isFinite(n) ? n : null;
  }

  // Handlers de verificación de teléfono
  const openManualVerify = () => {
    setVerifyError("");
    setVerifyLocalError("");
    const s = splitPhone(userData?.user?.phone);
    setVerifyCountry(s.country || countries[0]);
    setVerifyLocal(s.local || "");
    setOpenVerifyDialog(true);
  };

  const closeManualVerify = () => {
    if (!verifyLoading) setOpenVerifyDialog(false);
  };

  const composedE164 = buildE164(verifyCountry.dialCode, verifyLocal);

  const markPhoneVerified = async () => {
    const cleanLocal = String(verifyLocal || "").replace(/\D/g, "");
    if (!cleanLocal) {
      setVerifyLocalError("Ingresá solo dígitos del número local");
      return;
    }
    if (!isValidE164(composedE164)) {
      setVerifyLocalError("Número inválido. Verificá código de país y dígitos.");
      return;
    }

    try {
      setVerifyLoading(true);
      setVerifyError("");
      setVerifyLocalError("");

      await ApiService.updateUser(userId, {
        phone: composedE164,
        phone_verified: true,
      });

      // Actualizar estado local
      setUserData(prev => ({
        ...prev,
        user: {
          ...prev.user,
          phone: composedE164,
          phone_verified: true
        }
      }));
      setPhoneVerified(true);
      setVerifySuccess(true);
      setOpenVerifyDialog(false);
      notify("Teléfono verificado correctamente", "success");
    } catch (e) {
      if (e?.response?.data?.code === "PHONE_ALREADY_EXISTS") {
        setVerifyError("El número ya está en uso por otro usuario.");
      } else {
        setVerifyError("Error al verificar manualmente el teléfono.");
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const disablePhone = async () => {
    try {
      await ApiService.updateUser(userId, {
        phone: null,
        phone_verified: false,
      });

      // Actualizar estado local
      setUserData(prev => ({
        ...prev,
        user: {
          ...prev.user,
          phone: null,
          phone_verified: false
        }
      }));
      setPhoneVerified(false);
      setVerifySuccess(true);
      notify("Número deshabilitado correctamente", "success");
    } catch {
      notify("Error al deshabilitar el número", "error");
    }
  };

  // Handlers de gestión de pesos
  const handleOpenWeightDialog = (mode, weight = null) => {
    setWeightDialogMode(mode);
    if (mode === "edit" && weight) {
      setWeightFormData({
        current_weight: weight.current_weight || "",
        objective_weight: weight.objective_weight || "",
        date: weight.date ? new Date(weight.date).toISOString().split('T')[0] : ""
      });
      setEditingWeightId(weight.id);
    } else {
      setWeightFormData({
        current_weight: "",
        objective_weight: "",
        date: new Date().toISOString().split('T')[0]
      });
      setEditingWeightId(null);
    }
    setOpenWeightDialog(true);
  };

  const handleCloseWeightDialog = () => {
    setOpenWeightDialog(false);
    setEditingWeightId(null);
    setWeightFormData({
      current_weight: "",
      objective_weight: "",
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleWeightFieldChange = (field, value) => {
    setWeightFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveWeight = async () => {
    try {
      if (!weightFormData.current_weight) {
        notify("El peso actual es obligatorio", "warning");
        return;
      }

      if (weightDialogMode === "create") {
        await UserService.createWeight(userId, weightFormData);
        notify("Peso agregado correctamente", "success");
      } else {
        await UserService.updateWeight(userId, editingWeightId, weightFormData);
        notify("Peso actualizado correctamente", "success");
      }

      // Recargar datos
      const response = await UserService.getUserById(userId);
      if (response) {
        setUserData(response);
      }

      handleCloseWeightDialog();
    } catch (error) {
      console.error("Error al guardar peso:", error);
      notify("Error al guardar el peso", "error");
    }
  };

  const handleDeleteWeight = async (weightId) => {
    if (!window.confirm("¿Estás seguro de eliminar este registro de peso?")) {
      return;
    }

    try {
      await UserService.deleteWeight(userId, weightId);
      notify("Peso eliminado correctamente", "success");

      // Recargar datos
      const response = await UserService.getUserById(userId);
      if (response) {
        setUserData(response);
      }
    } catch (error) {
      console.error("Error al eliminar peso:", error);
      notify("Error al eliminar el peso", "error");
    }
  };

  // Handlers de modal de membresías
  const handleOpenMembershipModal = async () => {
    try {
      setLoadingPacks(true);
      setMembershipModalOpen(true);
      const res = await ProductService.getPack();
      setPacks(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error("Error al cargar packs:", error);
      notify("Error al cargar las membresías disponibles", "error");
    } finally {
      setLoadingPacks(false);
    }
  };

  const handleCloseMembershipModal = () => {
    setMembershipModalOpen(false);
    setSelectedMembershipId(null);
    setSelectedPack(null);
  };

  const handleSelectPack = (pack, disabled) => {
    if (disabled) return;
    const membershipId = pack?.membership?.id ?? null;
    setSelectedMembershipId(membershipId);
    setSelectedPack(pack);
  };

  const handleConfirmMembership = async () => {
    if (!selectedPack) return;

    const productId = selectedPack?.product?.ARG?.id;
    const membershipId = selectedPack?.membership?.id;

    if (!productId || !membershipId) {
      notify("Error: Datos de membresía incompletos", "error");
      return;
    }

    try {
      await PaymentsService.createPaymentManual({
        userIds: [userId],
        productId,
      });

      notify("Membresía asignada correctamente", "success");
      handleCloseMembershipModal();

      // Recargar datos del usuario
      const response = await UserService.getUserById(userId);
      if (response) {
        setUserData(response);
      }
    } catch (error) {
      console.error("Error al asignar membresía:", error);
      notify("Error al asignar la membresía", "error");
    }
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!userData || !userData.user) {
    return (
      <Box p={3}>
        <Alert severity="error">No se encontraron datos del usuario</Alert>
      </Box>
    );
  }

  const { user, weights = [], memberships = [], payments = [] } = userData;

  // Intentar con ambos formatos (camelCase y lowercase)
  const duoUser = (user.duouserid || user.duoUserId) ? {
    id: user.duouserid || user.duoUserId,
    name: user.duousername || user.duoUserName,
    lastname: user.duouserlastname || user.duoUserLastname,
    email: user.duouseremail || user.duoUserEmail,
  } : null;

  const sessionsTabIndex = 4;
  const duoTabIndex = duoUser ? 6 : null;

  // ==================== RENDER ====================
  return (
    <>
      <NotAllowed user={userPermission} page={"USERS"} />

      <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
        {/* ==================== HEADER ====================*/}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                src={user.avatar}
                alt={`${user.name} ${user.lastname}`}
                sx={{ width: 100, height: 100 }}
              />
            </Grid>

            <Grid item flex={1}>
              <Typography variant="h4" gutterBottom>
                {user.name} {user.lastname}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {user.admin && <Chip label="Admin" color="primary" size="small" />}
                {user.superAdmin && <Chip label="Super Admin" color="secondary" size="small" />}
                {isPremium && <Chip label="Premium" color="success" size="small" />}
                {user.deleted && <Chip label="Eliminado" color="error" size="small" />}
                {user.email_verified && <Chip label="Email Verificado" color="info" size="small" />}
                {user.phone_verified && <Chip label="Teléfono Verificado" color="info" size="small" />}
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>ID:</strong> {user.id} | <strong>Email:</strong> {user.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Creado:</strong> {formatDate(user.createdAt)} | <strong>Actualizado:</strong> {formatDate(user.updatedAt)}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Alias:</strong> {user.alias || "Sin alias"}
              </Typography>
            </Grid>

            <Grid item>
              {hasPermissionEditUser && (
              <Button
                variant={editMode ? "outlined" : "contained"}
                startIcon={editMode ? <CancelIcon /> : <EditIcon />}
                onClick={handleEditToggle}
                disabled={saving}
              >
                {editMode ? "Cancelar" : "Editar Perfil"}
              </Button>
              )}
              {editMode && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSaveUser}
                  disabled={saving}
                  sx={{ ml: 1 }}
                >
                  Guardar
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* ==================== TABS ====================*/}
        <Paper elevation={1} sx={{ mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<PersonIcon />} label="Datos Básicos" iconPosition="start" />
            <Tab icon={<MembershipIcon />} label="Membresías" iconPosition="start" />
            <Tab icon={<PaymentIcon />} label="Pagos" iconPosition="start" />
            <Tab icon={<ScaleIcon />} label="Pesos" iconPosition="start" />
            <Tab icon={<DevicesIcon />} label="Sesiones" iconPosition="start" />
            <Tab icon={<AdminPanelSettingsIcon />} label="Permisos" iconPosition="start" />

            {duoUser && <Tab icon={<PeopleIcon />} label="Dupla" iconPosition="start" />}
          </Tabs>
        </Paper>

        {/* ==================== TAB: DATOS BÁSICOS ====================*/}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContentMUI>
                  <Typography variant="h6" gutterBottom>
                    Información Personal
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        value={editedUser.name || ""}
                        onChange={(e) => handleFieldChange("name", e.target.value)}
                        disabled={!editMode}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Apellido"
                        value={editedUser.lastname || ""}
                        onChange={(e) => handleFieldChange("lastname", e.target.value)}
                        disabled={!editMode}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={editedUser.email || ""}
                        onChange={(e) => handleFieldChange("email", e.target.value)}
                        disabled={!editMode}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="DNI"
                        value={editedUser.dni || ""}
                        onChange={(e) => handleFieldChange("dni", e.target.value)}
                        disabled={!editMode}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Fecha de Nacimiento"
                        type="date"
                        value={editedUser.birth_date ? editedUser.birth_date.split('T')[0] : ""}
                        onChange={(e) => handleFieldChange("birth_date", e.target.value)}
                        disabled={!editMode}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!editedUser.deleted}
                            onChange={(e) => handleFieldChange("deleted", !e.target.checked)}
                            disabled={!editMode}
                            color="primary"
                          />
                        }
                        label={!editedUser.deleted ? "Usuario habilitado" : "Usuario deshabilitado"}
                      />
                    </Grid>
                  </Grid>
                </CardContentMUI>
              </Card>

              {/* PHONE MANAGEMENT */}
              <ManagePhone user={user} />

            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContentMUI>
                  <Typography variant="h6" gutterBottom>
                    Información Adicional
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Ubicación"
                        value={editedUser.user_location || ""}
                        onChange={(e) => handleFieldChange("user_location", e.target.value)}
                        disabled={!editMode}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Ocupación"
                        value={editedUser.occupation || ""}
                        onChange={(e) => handleFieldChange("occupation", e.target.value)}
                        disabled={!editMode}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Instagram"
                        value={editedUser.instagram || ""}
                        onChange={(e) => handleFieldChange("instagram", e.target.value)}
                        disabled={!editMode}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Talleres"
                        type="number"
                        value={editedUser.amountWorkshops || 0}
                        disabled
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </CardContentMUI>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* ==================== DIALOG: VERIFICACIÓN DE TELÉFONO ====================*/}
        <Dialog
          open={openVerifyDialog}
          onClose={closeManualVerify}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Verificación manual de teléfono</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Elegí el país y cargá el número local. Al presionar{" "}
              <strong>Aceptar</strong>, se actualizará el número y se marcará como{" "}
              <strong>verificado</strong>.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
              <TextField
                select
                fullWidth
                label="País / Código"
                value={verifyCountry?.dialCode || countries[0].dialCode}
                onChange={(e) => {
                  const next =
                    countries.find((c) => c.dialCode === e.target.value) ||
                    countries[0];
                  setVerifyCountry(next);
                }}
                disabled={verifyLoading}
                SelectProps={{
                  native: false,
                }}
              >
                {countries.map((c) => (
                  <MenuItem key={c.code} value={c.dialCode}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="Número local (solo dígitos)"
                value={verifyLocal}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, "");
                  setVerifyLocal(onlyDigits);
                  if (verifyLocalError) setVerifyLocalError("");
                }}
                error={!!verifyLocalError}
                helperText={
                  verifyLocalError || "Sin 0/15/9 automáticos; solo dígitos"
                }
                disabled={verifyLoading}
                inputProps={{ inputMode: "numeric" }}
              />
            </Stack>

            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">
                Vista previa E.164:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace", mt: 0.5 }}>
                {composedE164 || "—"}
              </Typography>
              {!isValidE164(composedE164) && (
                <Alert sx={{ mt: 1 }} severity="info">
                  El número debe tener entre 7 y 15 dígitos totales (incluyendo el
                  código de país).
                </Alert>
              )}
            </Box>

            {verifyError && (
              <Box mt={2}>
                <Alert severity="error">{verifyError}</Alert>
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={closeManualVerify} disabled={verifyLoading}>
              Cancelar
            </Button>
            <Button
              onClick={markPhoneVerified}
              variant="contained"
              disabled={verifyLoading}
              startIcon={<VerifiedIcon />}
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== TAB: MEMBRESÍAS ====================*/}
        {activeTab === 1 && (
          <Box>
            {/* Membresía Actual */}
            {currentMembership && (
              <Card elevation={3} sx={{ mb: 3, bgcolor: "success.light", color: "success.contrastText" }}>
                <CardContentMUI>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">
                        Membresía Actual
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>ID:</strong> {currentMembership.id}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Vigencia:</strong> {formatDate(currentMembership.start)} - {formatDate(currentMembership.end)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Asignada:</strong> {formatDate(currentMembership.assignedAt)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Nombre de producto:</strong> {currentMembership.products.length? currentMembership.products[0].name: "-"}
                      </Typography>
                    </Box>
                    <Chip
                      label={getMembershipStatus(currentMembership).label}
                      color={getMembershipStatus(currentMembership).color}
                      size="large"
                    />
                  </Stack>
                </CardContentMUI>
              </Card>
            )}

            {/* Botón para asignar membresía */}
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContentMUI>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6">
                      {currentMembership ? "Asignar Nueva Membresía" : "Sin Membresía Activa"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentMembership
                        ? "Agregar una membresía adicional o renovar"
                        : "Este usuario no tiene una membresía activa actualmente"}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenMembershipModal}
                  >
                    Asignar Membresía
                  </Button>
                </Stack>
              </CardContentMUI>
            </Card>

            {/* Historial de Membresías */}
            <Card elevation={2}>
              <CardContentMUI>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Historial de Membresías ({memberships.length})
                  </Typography>
                </Stack>

                {memberships.length === 0 ? (
                  <Alert severity="info">Este usuario no tiene membresías registradas</Alert>
                ) : (
                  <Stack spacing={2}>
                    {memberships.map((membership) => {
                      const status = getMembershipStatus(membership);
                      return (
                        <Paper key={membership.id} variant="outlined" sx={{ p: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={1}>
                              <Chip label={`ID: ${membership.id}`} size="small" />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <Typography variant="body2">
                                <strong>Inicio:</strong> {formatDate(membership.start)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <Typography variant="body2">
                                <strong>Fin:</strong> {formatDate(membership.end)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <Typography variant="body2">
                                <strong>Asignada:</strong> {formatDate(membership.assignedAt)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Typography variant="body2">
                                <strong>Nombre del producto:</strong> {membership.products.length? membership.products[0].name : "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={1}>
                              <Chip
                                label={status.label}
                                color={status.color}
                                size="small"
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </CardContentMUI>
            </Card>
          </Box>
        )}

        {/* ==================== TAB: PAGOS ====================*/}
        {activeTab === 2 && (
          <Card elevation={2}>
            <CardContentMUI>
              <Typography variant="h6" gutterBottom>
                Historial de Pagos ({payments.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {payments.length === 0 ? (
                <Alert severity="info">Este usuario no tiene pagos registrados</Alert>
              ) : (
                <Stack spacing={2}>
                  {payments.map((payment, index) => (
                    <Paper key={payment.id || index} variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Fecha:</strong> {formatDateTime(payment.createdAt)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Typography variant="body2">
                            <strong>Monto:</strong> ${payment.amount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Chip
                            label={payment.method || "N/A"}
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Chip
                            label={payment.status || "N/A"}
                            size="small"
                            color={
                              payment.status === "Approved" ? "success" :
                                payment.status === "Pending" ? "warning" : "error"
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Order ID:</strong> {payment.orderId}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContentMUI>
          </Card>
        )}

        {/* ==================== TAB: PESOS ====================*/}
        {activeTab === 3 && (
          <Card elevation={2}>
            <CardContentMUI>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Registro de Pesos ({weights.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenWeightDialog("create")}
                >
                  Agregar Peso
                </Button>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {weights.length === 0 ? (
                <Alert severity="info">No hay registros de peso para este usuario</Alert>
              ) : (
                <Stack spacing={2}>
                  {weights.map((weight, index) => (
                    <Paper key={weight.id || index} variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Fecha:</strong> {formatDate(weight.date)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Peso Actual:</strong> {weight.current_weight} kg
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Peso Objetivo:</strong> {weight.objective_weight ? `${weight.objective_weight} kg` : "No definido"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenWeightDialog("edit", weight)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteWeight(weight.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Grid>
                      </Grid>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Registrado: {formatDate(weight.createdAt)}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContentMUI>
          </Card>
        )}

        {/* ==================== TAB: SESIONES ====================*/}
        {activeTab === sessionsTabIndex && (
          <Box>
            <Card elevation={2} sx={{ mb: 2 }}>
              <CardContentMUI>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Sesiones por dispositivos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Estas sesiones pertenecen a la usuaria (no al Admin Client).
                    </Typography>
                  </Box>

                  {deviceSessionsStats ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={`Total: ${deviceSessionsStats?.totalSessions ?? 0}`}
                        variant="outlined"
                      />
                      <Chip
                        label={`Activas: ${deviceSessionsStats?.activeSessions ?? 0}`}
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        label={`Web: ${deviceSessionsStats?.devices?.web ?? 0}`}
                        variant="outlined"
                      />
                      <Chip
                        label={`Mobile: ${deviceSessionsStats?.devices?.mobile ?? 0}`}
                        variant="outlined"
                      />
                      <Chip
                        label={`Último acceso: ${formatDateTime(
                          deviceSessionsStats?.lastAccessAt
                        )}`}
                        variant="outlined"
                      />
                    </Stack>
                  ) : null}
                </Stack>
              </CardContentMUI>
            </Card>

            {sessionsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="25vh">
                <CircularProgress />
              </Box>
            ) : sessionsError ? (
              <Alert severity="error">{sessionsError}</Alert>
            ) : deviceSessions.length === 0 ? (
              <Alert severity="info">Esta usuaria no tiene sesiones registradas.</Alert>
            ) : (
              <Card elevation={2}>
                <CardContentMUI>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Listado de sesiones ({deviceSessions.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Tipo</strong></TableCell>
                          <TableCell><strong>Activa</strong></TableCell>
                          <TableCell><strong>Último acceso</strong></TableCell>
                          <TableCell><strong>Fingerprint</strong></TableCell>
                          <TableCell><strong>IP</strong></TableCell>
                          <TableCell><strong>Modelo</strong></TableCell>
                          <TableCell><strong>OS</strong></TableCell>
                          <TableCell><strong>App</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {deviceSessions.map((s) => (
                          <TableRow key={s.id}>
                            <TableCell>
                              {(() => {
                                const formFactor = s?.metadata?.formFactor;
                                const label = formFactor === "tablet" ? "tablet" : (s.deviceType || "N/A");
                                return (
                                  <Chip
                                    size="small"
                                    label={label}
                                    variant="outlined"
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={s.active ? "Sí" : "No"}
                                color={s.active ? "success" : "default"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{formatDateTime(s.lastAccessAt)}</TableCell>
                            <TableCell title={s.fingerprintHash || ""}>
                              {shortHash(s.fingerprintHash)}
                            </TableCell>
                            <TableCell>{s.ipAddress || "-"}</TableCell>
                            <TableCell>{s.deviceModel || "-"}</TableCell>
                            <TableCell>{s.osVersion || "-"}</TableCell>
                            <TableCell>{s.appVersion || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContentMUI>
              </Card>
            )}
          </Box>
        )}

        {/* ===================== PERMISOS ==================================== */}

        {activeTab === 5 && (

          <UserPermissionsTable user={user} />
        )}



        {/* ==================== DIALOG: AGREGAR/EDITAR PESO ====================*/}
        <Dialog
          open={openWeightDialog}
          onClose={handleCloseWeightDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {weightDialogMode === "create" ? "Agregar Peso" : "Editar Peso"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Peso Actual (kg) *"
                type="number"
                value={weightFormData.current_weight}
                onChange={(e) => handleWeightFieldChange("current_weight", e.target.value)}
                inputProps={{ step: "0.1", min: "0" }}
                required
              />

              <TextField
                fullWidth
                label="Peso Objetivo (kg) - Opcional"
                type="number"
                value={weightFormData.objective_weight}
                onChange={(e) => handleWeightFieldChange("objective_weight", e.target.value)}
                inputProps={{ step: "0.1", min: "0" }}
              />

              <TextField
                fullWidth
                label="Fecha"
                type="date"
                value={weightFormData.date}
                onChange={(e) => handleWeightFieldChange("date", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseWeightDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveWeight}
              variant="contained"
              startIcon={<SaveIcon />}
            >
              {weightDialogMode === "create" ? "Agregar" : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== TAB: DUPLA ====================*/}
        {duoUser && duoTabIndex !== null && activeTab === duoTabIndex && (
          <Card elevation={2}>
            <CardContentMUI>
              <Typography variant="h6" gutterBottom>
                Información de la Dupla
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <Avatar sx={{ width: 80, height: 80 }}>
                    {duoUser.name?.[0]}{duoUser.lastname?.[0]}
                  </Avatar>
                </Grid>

                <Grid item flex={1}>
                  <Typography variant="h5">
                    {duoUser.name} {duoUser.lastname}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong> {duoUser.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>ID:</strong> {duoUser.id}
                  </Typography>
                </Grid>

                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={() => window.open(`/usuarios/${duoUser.id}`, '_blank')}
                  >
                    Ver Perfil Completo
                  </Button>
                </Grid>
              </Grid>
            </CardContentMUI>
          </Card>
        )}


        {/* ==================== DIALOG: ASIGNAR MEMBRESÍA ====================*/}
        <Dialog open={membershipModalOpen} onClose={handleCloseMembershipModal} maxWidth="md" fullWidth>
          <DialogTitle>Asignar membresía</DialogTitle>
          <DialogContent dividers>
            {loadingPacks ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {packs.map((pack, idx) => {
                  const membershipId = pack?.membership?.id ?? idx;
                  const available = isPackAvailable(pack);
                  const isSelectedPack = selectedMembershipId === membershipId;
                  const disabledReason =
                    String(pack?.product?.type || "").toLowerCase() === "disabled"
                      ? "Este pack está deshabilitado."
                      : "Este pack no está disponible en este momento.";

                  return (
                    <Grid item xs={12} sm={6} key={membershipId}>
                      <Tooltip
                        title={available ? "" : disabledReason}
                        placement="top"
                        arrow
                        disableHoverListener={available}
                      >
                        <Card
                          elevation={isSelectedPack ? 6 : 2}
                          sx={(theme) => ({
                            borderRadius: 3,
                            transition: "all .2s ease",
                            outline: isSelectedPack
                              ? `2px solid ${theme.palette.primary.main}`
                              : `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                            bgcolor: available ? "background.paper" : alpha("#000", 0.02),
                            opacity: available ? 1 : 0.6,
                          })}
                        >
                          <CardActionArea
                            onClick={() => handleSelectPack(pack, !available)}
                            disabled={!available}
                            sx={{ p: 1, borderRadius: 3 }}
                          >
                            <CardContentMUI>
                              <Stack direction="row" alignItems="flex-start" spacing={1}>
                                <Radio
                                  checked={isSelectedPack}
                                  value={membershipId}
                                  disabled={!available}
                                  sx={{ mt: 0.5 }}
                                  color="primary"
                                />
                                <Box flex={1}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    gap={1}
                                  >
                                    <Typography variant="subtitle1" fontWeight={700}>
                                      {pack?.product?.name}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      color={availabilityLabel(pack) === "Disponible" ? "success" : "default"}
                                      label={availabilityLabel(pack)}
                                    />
                                  </Stack>

                                  <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                                    {pack?.product?.description}
                                  </Typography>

                                  <Stack direction="row" spacing={2} sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`ARS ${formatCurrencyPack(pack?.product?.ARG?.price, "ARS")}`}
                                    />
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`USD ${formatCurrencyPack(pack?.product?.DOLARS?.price, "USD")}`}
                                    />
                                  </Stack>

                                  <Stack direction="row" spacing={1.5} sx={{ mt: 1.25 }} useFlexGap flexWrap="wrap">
                                    <Typography variant="caption">
                                      Vigencia: {formatDateShort(pack?.membership?.start)} — {formatDateShort(pack?.membership?.end)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                      {pack?.workshops?.length > 0
                                        ? `Incluye ${pack.workshops.length} taller(es)`
                                        : "Incluye todos los talleres del año"}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Stack>
                            </CardContentMUI>
                          </CardActionArea>
                        </Card>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMembershipModal}>Cancelar</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmMembership}
              disabled={!selectedPack}
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default ProfileUser;
